import { isNumber } from '../types/isNumber';

export type FormatNumberOptions = {
  prefix?: string;
  suffix?: string;
  fallback?: string;
} & Intl.NumberFormatOptions;

/**
 * Formats a number according to the specified formatting options.
 *
 * @param value - The number to format.
 * @param numberFormattingOptions - Optional. An object containing options to customize the number formatting.
 * @returns The formatted number as a string, or `undefined` if the input is not a valid number.
 *
 * @example
 * ```typescript
 * formatNumber(1234567); // "1.23M"
 * formatNumber(1234567, { maximumFractionDigits: 1 }); // "1.2M"
 * ```
 *
 * @default { notation: 'compact', maximumFractionDigits: 2 }
 */
export function formatNumber(
  value: number | string | undefined,
  options: FormatNumberOptions & { fallback: string },
): string;
export function formatNumber(
  value?: number | string,
  options?: FormatNumberOptions,
): string | undefined;
export function formatNumber(
  value: number | string = '',
  options?: FormatNumberOptions,
) {
  const number = Number.parseFloat(String(value));
  if (!isNumber(number)) return options?.fallback;

  const formatted = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  return `${options?.prefix ?? ''}${formatted}${options?.suffix ?? ''}`;
}
